import React from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Brain,
  Activity,
  Zap,
  Coins,
  BarChart3,
  Sparkles,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import { api } from '../lib/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MemoryEntry {
  content: string
  type: string
  confidence: number
  created_at: string
}

interface SessionEntry {
  id: string
  name: string
  status: string
  startedAt: string | null
  duration: number | null
}

interface SkillTop {
  skill: string
  count: number
  lastUsed: string
}

interface SkillInvocation {
  skill: string
  timestamp: string
  duration: number | null
}

interface TokenCall {
  provider: string
  model: string
  tokens: number
  timestamp: string
}

interface ToolTrigram {
  sequence: string[]
  count: number
}

interface RepeatedPrompt {
  prompt: string
  count: number
}

interface InstinctEntry {
  name: string
  confidence: number
  domain?: string
  lastValidated?: string
}

interface ConsciousnessData {
  memory: {
    totalEntries: number
    recentEntries: MemoryEntry[]
    categoryCounts: Record<string, number>
  }
  sessions: {
    active: number
    completed: number
    queued: number
    failed: number
    recent: SessionEntry[]
  }
  skills: {
    totalInvocations: number
    topSkills: SkillTop[]
    recentInvocations: SkillInvocation[]
  }
  tokens: {
    totalInput: number
    totalOutput: number
    estimatedCost: number
    byProvider: Record<string, { input: number; output: number; cost: number }>
    recentCalls: TokenCall[]
  }
  patterns: {
    taskTypes: Record<string, number>
    repeatedPrompts: RepeatedPrompt[]
    toolTrigrams: ToolTrigram[]
  }
  instincts: {
    total: number
    byConfidence: Record<string, number>
    recent: InstinctEntry[]
  }
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

const PIE_COLORS = [
  '#6366f1', '#22d3ee', '#f59e0b', '#ef4444', '#10b981',
  '#8b5cf6', '#ec4899', '#14b8a6',
]

const CONFIDENCE_COLORS: Record<string, string> = {
  '0.7+': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  '0.5-0.7': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  '<0.5': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  queued: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Card({ title, icon: Icon, children }: {
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-800 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-primary-500 dark:text-primary-400" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
          {title}
        </h2>
      </div>
      {children}
    </div>
  )
}

function Badge({ text, className }: { text: string; className: string }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {text}
    </span>
  )
}

function StatNumber({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="text-center">
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
    </div>
  )
}

function confidenceBadgeClass(confidence: number): string {
  if (confidence >= 0.7) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
  if (confidence >= 0.5) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
}

function formatCost(usd: number): string {
  if (usd < 0.01) return '<$0.01'
  return `$${usd.toFixed(2)}`
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function MemoryPanel({ data }: { data: ConsciousnessData['memory'] }) {
  const pieData = Object.entries(data.categoryCounts).map(([name, value]) => ({ name, value }))

  return (
    <Card title="Memory" icon={Brain}>
      <div className="flex items-center gap-6 mb-4">
        <StatNumber value={data.totalEntries} label="Total entries" />
        <div className="flex-1 h-32">
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={50} innerRadius={25}>
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'var(--tooltip-bg, #1f2937)', border: 'none', borderRadius: '8px', color: '#f3f4f6' }} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center pt-8">No memory data</p>
          )}
        </div>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {data.recentEntries.slice(0, 8).map((entry, i) => (
          <div key={i} className="flex items-start gap-2 text-xs">
            <Badge text={entry.type} className={confidenceBadgeClass(entry.confidence)} />
            <span className="text-gray-600 dark:text-gray-300 line-clamp-2 flex-1">{entry.content}</span>
            <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">{timeAgo(entry.created_at)}</span>
          </div>
        ))}
        {data.recentEntries.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">No recent entries</p>
        )}
      </div>
    </Card>
  )
}

function SessionsPanel({ data }: { data: ConsciousnessData['sessions'] }) {
  return (
    <Card title="Sessions" icon={Activity}>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatNumber value={data.active} label="Active" />
        <StatNumber value={data.completed} label="Done" />
        <StatNumber value={data.queued} label="Queued" />
        <StatNumber value={data.failed} label="Failed" />
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {data.recent.map((s) => (
          <div key={s.id} className="flex items-center gap-2 text-xs">
            <Badge
              text={s.status}
              className={STATUS_COLORS[s.status] ?? STATUS_COLORS.queued ?? ''}
            />
            <span className="text-gray-700 dark:text-gray-300 truncate flex-1">{s.name || s.id.slice(0, 8)}</span>
            {s.duration != null && (
              <span className="text-gray-400 dark:text-gray-500">{(s.duration / 1000).toFixed(1)}s</span>
            )}
            {s.startedAt && (
              <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">{timeAgo(s.startedAt)}</span>
            )}
          </div>
        ))}
        {data.recent.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">No sessions recorded</p>
        )}
      </div>
    </Card>
  )
}

function SkillsPanel({ data }: { data: ConsciousnessData['skills'] }) {
  const barData = data.topSkills.map(s => ({
    name: s.skill.length > 18 ? s.skill.slice(0, 18) + '...' : s.skill,
    count: s.count,
  }))

  return (
    <Card title="Skill Usage" icon={Zap}>
      <StatNumber value={data.totalInvocations} label="Total invocations" />
      <div className="mt-4 h-48">
        {barData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--tooltip-bg, #1f2937)', border: 'none', borderRadius: '8px', color: '#f3f4f6' }} />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center pt-16">No skill data</p>
        )}
      </div>
      <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
        {data.recentInvocations.slice(0, 6).map((inv, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="text-primary-600 dark:text-primary-400 font-medium">{inv.skill}</span>
            {inv.duration != null && (
              <span className="text-gray-400 dark:text-gray-500">{(inv.duration / 1000).toFixed(1)}s</span>
            )}
            <span className="ml-auto text-gray-400 dark:text-gray-500 whitespace-nowrap">{timeAgo(inv.timestamp)}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

function TokensPanel({ data }: { data: ConsciousnessData['tokens'] }) {
  const providerData = Object.entries(data.byProvider).map(([name, v]) => ({
    name,
    input: v.input,
    output: v.output,
    cost: v.cost,
  }))

  return (
    <Card title="Token Economics" icon={Coins}>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatNumber value={formatTokens(data.totalInput)} label="Input" />
        <StatNumber value={formatTokens(data.totalOutput)} label="Output" />
        <StatNumber value={formatCost(data.estimatedCost)} label="Cost" />
      </div>
      <div className="h-40">
        {providerData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={providerData} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v: number) => formatTokens(v)} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--tooltip-bg, #1f2937)', border: 'none', borderRadius: '8px', color: '#f3f4f6' }}
                formatter={(value: unknown) => formatTokens(value as number)}
              />
              <Bar dataKey="input" fill="#6366f1" name="Input" stackId="tokens" radius={[0, 0, 0, 0]} />
              <Bar dataKey="output" fill="#22d3ee" name="Output" stackId="tokens" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center pt-16">No token data</p>
        )}
      </div>
      <div className="mt-3 space-y-1.5 max-h-28 overflow-y-auto">
        {data.recentCalls.slice(0, 5).map((call, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="text-gray-700 dark:text-gray-300 font-medium">{call.provider}</span>
            <span className="text-gray-500 dark:text-gray-400">{call.model}</span>
            <span className="ml-auto text-gray-400 dark:text-gray-500">{formatTokens(call.tokens)} tok</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

function PatternsPanel({ data }: { data: ConsciousnessData['patterns'] }) {
  const taskData = Object.entries(data.taskTypes)
    .sort(([, a], [, b]) => b - a)
    .map(([name, value]) => ({ name, value }))

  return (
    <Card title="Behavioral Patterns" icon={BarChart3}>
      <div className="h-40 mb-4">
        {taskData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={taskData} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--tooltip-bg, #1f2937)', border: 'none', borderRadius: '8px', color: '#f3f4f6' }} />
              <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center pt-16">No observation data</p>
        )}
      </div>

      {data.repeatedPrompts.length > 0 && (
        <div className="mb-3">
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Could Be Skills</h3>
          <div className="space-y-1">
            {data.repeatedPrompts.slice(0, 5).map((rp, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <Badge text={`${rp.count}x`} className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" />
                <span className="text-gray-600 dark:text-gray-300 truncate">{rp.prompt}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.toolTrigrams.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Top Tool Sequences</h3>
          <div className="space-y-1.5">
            {data.toolTrigrams.slice(0, 5).map((tri, i) => (
              <div key={i} className="flex items-center gap-1 text-xs">
                <Badge text={`${tri.count}x`} className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300" />
                {tri.sequence.map((tool, j) => (
                  <span key={j} className="flex items-center gap-1">
                    {j > 0 && <span className="text-gray-400 dark:text-gray-500">&rarr;</span>}
                    <span className="text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{tool}</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {taskData.length === 0 && data.repeatedPrompts.length === 0 && data.toolTrigrams.length === 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">No pattern data available</p>
      )}
    </Card>
  )
}

function InstinctsPanel({ data }: { data: ConsciousnessData['instincts'] }) {
  return (
    <Card title="Instincts" icon={Sparkles}>
      <div className="flex items-center gap-6 mb-4">
        <StatNumber value={data.total} label="Total" />
        <div className="flex gap-2">
          {Object.entries(data.byConfidence).map(([tier, count]) => (
            <div key={tier} className="text-center">
              <Badge text={`${count}`} className={CONFIDENCE_COLORS[tier] ?? CONFIDENCE_COLORS['<0.5'] ?? ''} />
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{tier}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {data.recent.map((inst, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <Badge text={inst.confidence.toFixed(1)} className={confidenceBadgeClass(inst.confidence)} />
            <span className="text-gray-700 dark:text-gray-300 font-medium flex-1 truncate">{inst.name}</span>
            {inst.domain && (
              <span className="text-gray-400 dark:text-gray-500">{inst.domain}</span>
            )}
            {inst.lastValidated && (
              <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">{timeAgo(inst.lastValidated)}</span>
            )}
          </div>
        ))}
        {data.recent.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">No instincts found</p>
        )}
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function ConsciousnessPage() {
  const { data, isLoading, error } = useQuery<ConsciousnessData>({
    queryKey: ['consciousness'],
    queryFn: () => api.consciousness.get() as Promise<ConsciousnessData>,
    refetchInterval: 30_000,
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-black tracking-wide text-gray-900 dark:text-white uppercase">
          Consciousness
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Agent observability: memory, sessions, skills, costs, and behavioral patterns
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-700 dark:text-red-300">
            Failed to load consciousness data: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <MemoryPanel data={data.memory} />
          <SessionsPanel data={data.sessions} />
          <SkillsPanel data={data.skills} />
          <TokensPanel data={data.tokens} />
          <PatternsPanel data={data.patterns} />
          <InstinctsPanel data={data.instincts} />
        </div>
      )}
    </div>
  )
}
